import { useState, useEffect, useMemo } from 'react';
import { SupabaseAdminService } from '@/services/supabase-admin';
import { useSupabase } from '@/contexts/supabase/SupabaseClientProvider';
import { useAuth } from '@/hooks/use-auth';

interface AdminStats {
  totalUsers: number;
  adminCount: number;
  usersThisMonth: number;
  totalGroups: number;
  subgroups: number;
  newGroupsThisMonth: number;
  totalEvents: number;
  eventsThisMonth: number;
  contactComplete: number;
  missingEmail: number;
  missingPhone: number;
  loading: boolean;
}

export function useAdminStats() {
  const supabase = useSupabase();
  const { isAuthenticated } = useAuth();
  const adminService = useMemo(() => new SupabaseAdminService(supabase), [supabase]);
  const [stats, setStats] = useState<AdminStats>({
    totalUsers: 0,
    adminCount: 0,
    usersThisMonth: 0,
    totalGroups: 0,
    subgroups: 0,
    newGroupsThisMonth: 0,
    totalEvents: 0,
    eventsThisMonth: 0,
    contactComplete: 0,
    missingEmail: 0,
    missingPhone: 0,
    loading: true
  });

  useEffect(() => {
    const fetchStats = async () => {
      if (!isAuthenticated) {
        setStats(prev => ({ ...prev, loading: true }));
        return;
      }
      try {
        setStats(prev => ({ ...prev, loading: true }));
        
        if (process.env.NODE_ENV === 'development') {
          console.log('Fetching admin stats...');
        }
        
        const [
          totalUsers,
          adminCount,
          usersThisMonth,
          groupStats,
          newGroupsThisMonth,
          totalEvents,
          eventsThisMonth,
          contactStats
        ] = await Promise.all([
          adminService.getTotalUsers(),
          adminService.getAdminCount(),
          adminService.getUsersThisMonth(),
          adminService.getGroupStats(),
          adminService.getNewGroupsThisMonth(),
          adminService.getTotalEvents(),
          adminService.getEventsThisMonth(),
          adminService.getContactStats()
        ]);
        
        if (process.env.NODE_ENV === 'development') {
          console.log('Admin stats fetched:', {
            totalUsers,
            usersThisMonth,
            groupStats,
            newGroupsThisMonth,
            totalEvents,
            eventsThisMonth
          });
        }
        
        setStats({
          totalUsers,
          usersThisMonth,
          adminCount,
          totalGroups: groupStats.totalGroups,
          subgroups: groupStats.subgroups,
          newGroupsThisMonth,
          totalEvents,
          eventsThisMonth,
          contactComplete: contactStats.complete,
          missingEmail: Math.max(contactStats.total - contactStats.withEmail, 0),
          missingPhone: Math.max(contactStats.total - contactStats.withPhone, 0),
          loading: false
        });
      } catch (error) {
        console.error('Error fetching admin stats:', error);
        setStats(prev => ({ ...prev, loading: false }));
      }
    };

    fetchStats();
  }, [adminService, isAuthenticated]);

  return stats;
}
